import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useActions } from '../../../providers/actions-provider/actions.context';
import {
  POLL_BASE_MS,
  POLL_HIDDEN_MS,
  POLL_MAX_FAILURES,
  POLL_MAX_MS,
} from '../../../../utils/asset-type-import/constants';
import { IImportJob, isTerminalJob } from '../../../../utils/asset-type-import/i-import-job';

/**
 * Watches a server-side import job. [AVP-891]
 *
 * A chained setTimeout, never setInterval: a slow status response must not
 * stack requests against a service whose worker already holds a connection
 * from the same pool. The next poll is scheduled only once the previous one
 * has settled.
 *
 * Losing contact is reported as exactly that, never as a failed import — the
 * job runs on the server and may well finish while the browser is sulking.
 * "Import failed" there would be a lie the user might act on.
 */

export const LOST_CONTACT = 'Lost contact with the server. The import may still be running.';

export interface IUseImportJob {
  job: IImportJob | null;
  /** set only when polling gave up; the job itself is probably fine */
  pollError: string | null;
  retry: () => void;
}

const isHidden = (): boolean => (
  typeof document !== 'undefined' && document.visibilityState === 'hidden'
);

export const useImportJob = (jobId: string | null): IUseImportJob => {
  const { assetActions } = useActions();

  const [job, setJob] = useState<IImportJob | null>(null);
  const [pollError, setPollError] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /*
   * Held in a ref, deliberately out of the effect's dependencies.
   * useGetActions memoises assetActions on [state, dispatch, api], and `state`
   * is the whole store — so its identity changes on every dispatch anywhere in
   * the app. Depending on it would tear down and restart this loop (with an
   * immediate poll each time) whenever any unrelated action fired.
   */
  const actions = useRef(assetActions);
  actions.current = assetActions;

  const clearTimer = useCallback((): void => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  useEffect(() => {
    if (!jobId) {
      clearTimer();
      setJob(null);
      setPollError(null);
      return undefined;
    }

    // local to this run of the effect, so a response that arrives after the
    // job changed or the modal closed cannot write stale state
    let cancelled = false;
    let failures = 0;
    let delay = POLL_BASE_MS;

    setPollError(null);

    const schedule = (ms: number): void => {
      clearTimer();
      timer.current = setTimeout(() => {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        poll();
      }, ms);
    };

    const poll = async (): Promise<void> => {
      if (cancelled) {
        return;
      }

      try {
        const next = await actions.current.fetchImportJob(jobId);
        if (cancelled) {
          return;
        }

        setJob(next);
        setPollError(null);
        failures = 0;
        delay = POLL_BASE_MS;

        if (isTerminalJob(next.state)) {
          clearTimer();
          return;
        }

        schedule(isHidden() ? POLL_HIDDEN_MS : delay);
      } catch {
        if (cancelled) {
          return;
        }

        failures += 1;
        if (failures >= POLL_MAX_FAILURES) {
          clearTimer();
          setPollError(LOST_CONTACT);
          return;
        }

        delay = Math.min(delay * 2, POLL_MAX_MS);
        schedule(delay);
      }
    };

    poll();

    return (): void => {
      cancelled = true;
      clearTimer();
    };
  }, [jobId, retryNonce, clearTimer]);

  /** Re-runs the effect, which restarts the loop with a fresh backoff. */
  const retry = useCallback((): void => setRetryNonce((nonce) => nonce + 1), []);

  return { job, pollError, retry };
};

export default useImportJob;
